import { useAuth0 } from '@auth0/auth0-react'
import { Avatar, Group, Menu, Text, UnstyledButton } from '@mantine/core'
import {
    IconChevronRight,
    IconLogout,
    IconUsersGroup,
} from '@tabler/icons-react'
import { type ReactNode } from 'react'
import { useGetAuthClaims } from '@/hooks/useGetAuthClaims'

interface UserButtonProps extends React.ComponentPropsWithoutRef<'button'> {
    name: string
    image?: string
    email?: string
    icon?: React.ReactNode
}

const UserButton = ({
    ref,
    image,
    name,
    email,
    icon,
    ...others
}: UserButtonProps & { ref?: React.RefObject<HTMLButtonElement | null> }) => (
    <UnstyledButton
        ref={ref}
        style={{
            padding: 'var(--mantine-spacing-sm)',
            color: 'var(--mantine-color-text)',
            borderRadius: 'var(--mantine-radius-sm)',
        }}
        {...others}
    >
        <Group>
            <Avatar src={image} radius='xl' />

            <div style={{ flex: 1 }}>
                <Text size='sm' fw={500}>
                    {name}
                </Text>

                {email && (
                    <Text c='dimmed' size='xs'>
                        {email}
                    </Text>
                )}
            </div>

            {icon || <IconChevronRight size={16} />}
        </Group>
    </UnstyledButton>
)

export interface UserProfileProps {
    menuItems?: ReactNode[]
}

export function UserProfile({ menuItems }: UserProfileProps) {
    const { user, isLoading, logout } = useAuth0()

    if (isLoading || !user) {
        return <div>Loading...</div>
    }

    return (
        <Menu
            position='bottom-end'
            offset={-1}
            withArrow
            arrowPosition='center'
        >
            <Menu.Target>
                <UserButton
                    image={user.picture}
                    name={user.name ?? user.nickname ?? 'Unknown User'}
                    email={user.email}
                />
            </Menu.Target>
            <Menu.Dropdown>
                {menuItems}
                <Menu.Item
                    color='error'
                    leftSection={<IconLogout size={14} />}
                    onClick={() =>
                        void logout({
                            logoutParams: {
                                returnTo: window.location.origin,
                            },
                        })
                    }
                >
                    Logout
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    )
}

export function OrganizationProfile() {
    const { user, isLoading } = useAuth0()

    const { data: idTokenClaims } = useGetAuthClaims()

    if (isLoading || !user) {
        return <div>Loading...</div>
    }

    return (
        <Menu
            position='bottom-start'
            offset={-1}
            withArrow
            arrowPosition='center'
        >
            <Menu.Target>
                <UnstyledButton>{idTokenClaims?.org_name}</UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item leftSection={<IconUsersGroup size={14} />}>
                    Change Organization
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    )
}
